import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { load, save } from '../lib/storage';
import { endsFromWaypoints, finishOf, NO_ENDS, type TripEnds, type TripPoint } from '../lib/tripEnds';
import type { PlaceSummary, TripKind, Waypoint } from '../lib/types';

export interface TripState extends TripEnds {
  name: string;
  /** Road trip, day out or bar hop. Only a bar hop needs the driver and the pledge. */
  kind: TripKind;
  driver: string;
  pledged: boolean;
  date: string; // yyyy-mm-dd or ''
  stops: PlaceSummary[];
  /** Stops reached on this outing (punched on the ticket), in the order they were reached. */
  punched: string[];
  /** Made it to the end point after the last stop. */
  finished: boolean;
}

/** Where the drive is headed next: the first stop not punched, then the end point. */
export type Heading = { kind: 'stop'; place: PlaceSummary } | { kind: 'end'; point: TripPoint } | null;

/** The trip kind to set when adding stops: a kind, or one worked out from the stops after the add. */
export type AddAs = TripKind | ((stops: PlaceSummary[]) => TripKind);

interface TripContextValue {
  trip: TripState;
  update: (patch: Partial<TripState>) => void;
  add: (place: PlaceSummary, as?: AddAs) => void;
  remove: (id: string) => void;
  move: (index: number, delta: -1 | 1) => void;
  /** New stops (the ticket starts over). Pass ends to replace the start and end too. */
  replaceStops: (places: PlaceSummary[], ends?: TripEnds, as?: AddAs) => void;
  setEnds: (patch: Partial<TripEnds>) => void;
  clear: () => void;
  has: (id: string) => boolean;
  toggle: (place: PlaceSummary, as?: AddAs) => void;
  /** The first stop not punched yet, or null when the ticket is done (or empty). */
  nextStop: PlaceSummary | null;
  heading: Heading;
  finish: () => void;
  punch: (id: string) => void;
  unpunch: (id: string) => void;
  /** Passport: places the user has stamped. */
  stamps: Record<string, { name: string; place: string; at: string }>;
  stamp: (place: PlaceSummary) => void;
  unstamp: (id: string) => void;
}

const EMPTY: TripState = { name: '', kind: 'DayOut', driver: '', pledged: false, date: '', stops: [], ...NO_ENDS, punched: [], finished: false };
const TRIP_KEY = 'bbh.trip.v2';
const STAMP_KEY = 'bbh.stamps.v2';

const TripContext = createContext<TripContextValue | null>(null);

// A new set of stops is a new outing: "my location" is free to move again.
function newOuting(start: TripPoint | null): TripPoint | null {
  if (!start?.setOffAt) return start;
  const { setOffAt: _, ...rest } = start;
  return rest;
}

const kindOf = (stops: PlaceSummary[], as: AddAs) => (typeof as === 'function' ? as(stops) : as);

// Trips saved before start/end existed kept them as a waypoints list. Trips saved before kinds
// existed were all bar hops; one with a driver named stays one, an empty one starts as a day out.
function loadTrip(): TripState {
  const { waypoints, ...stored } = load<Partial<TripState> & { waypoints?: Waypoint[] }>(TRIP_KEY, {});
  const ends = 'start' in stored ? {} : endsFromWaypoints(waypoints);
  const kind: TripKind = stored.kind ?? (stored.driver?.trim() ? 'BarHop' : 'DayOut');
  return { ...EMPTY, ...stored, ...ends, kind };
}

export function TripProvider({ children }: { children: ReactNode }) {
  const [trip, setTrip] = useState<TripState>(loadTrip);
  const [stamps, setStamps] = useState<TripContextValue['stamps']>(() => load(STAMP_KEY, {}));

  useEffect(() => save(TRIP_KEY, trip), [trip]);
  useEffect(() => save(STAMP_KEY, stamps), [stamps]);

  const update = useCallback((patch: Partial<TripState>) => setTrip((t) => ({ ...t, ...patch })), []);

  const add = useCallback(
    (place: PlaceSummary, as?: AddAs) =>
      setTrip((t) => {
        if (t.stops.some((s) => s.id === place.id)) return t;
        const stops = [...t.stops, place];
        return { ...t, stops, finished: false, kind: as ? kindOf(stops, as) : t.kind };
      }),
    [],
  );

  const remove = useCallback(
    (id: string) =>
      setTrip((t) => ({ ...t, stops: t.stops.filter((s) => s.id !== id), punched: t.punched.filter((p) => p !== id), finished: false })),
    [],
  );

  const move = useCallback(
    (index: number, delta: -1 | 1) =>
      setTrip((t) => {
        const target = index + delta;
        if (target < 0 || target >= t.stops.length) return t;
        const stops = [...t.stops];
        [stops[index], stops[target]] = [stops[target], stops[index]];
        return { ...t, stops, finished: false };
      }),
    [],
  );

  const replaceStops = useCallback(
    (places: PlaceSummary[], ends?: TripEnds, as?: AddAs) =>
      setTrip((t) => {
        const next = { ...t, ...ends, stops: places, punched: [], finished: false, kind: as ? kindOf(places, as) : t.kind };
        return { ...next, start: newOuting(next.start) };
      }),
    [],
  );

  const setEnds = useCallback((patch: Partial<TripEnds>) => setTrip((t) => ({ ...t, ...patch, finished: false })), []);

  // Clearing the stops keeps the driver and where you start and finish; the kind starts over.
  const clear = useCallback(
    () => setTrip((t) => ({ ...EMPTY, driver: t.driver, start: newOuting(t.start), end: t.end, endAtStart: t.endAtStart })),
    [],
  );

  const punch = useCallback(
    (id: string) => setTrip((t) => (t.punched.includes(id) ? t : { ...t, punched: [...t.punched, id] })),
    [],
  );
  const unpunch = useCallback((id: string) => setTrip((t) => ({ ...t, punched: t.punched.filter((p) => p !== id), finished: false })), []);
  const finish = useCallback(() => setTrip((t) => ({ ...t, finished: true })), []);

  const value = useMemo<TripContextValue>(() => {
    const ids = new Set(trip.stops.map((s) => s.id));
    const punched = new Set(trip.punched);
    const nextStop = trip.stops.find((s) => !punched.has(s.id)) ?? null;
    const end = finishOf(trip);
    const heading: Heading = nextStop
      ? { kind: 'stop', place: nextStop }
      : end && trip.stops.length && !trip.finished
        ? { kind: 'end', point: end }
        : null;
    return {
      trip,
      update,
      add,
      remove,
      move,
      replaceStops,
      setEnds,
      clear,
      has: (id) => ids.has(id),
      toggle: (place, as) => (ids.has(place.id) ? remove(place.id) : add(place, as)),
      nextStop,
      heading,
      finish,
      punch,
      unpunch,
      stamps,
      stamp: (place) =>
        setStamps((s) => ({
          ...s,
          [place.id]: { name: place.name, place: [place.town, place.state].filter(Boolean).join(', '), at: new Date().toISOString() },
        })),
      unstamp: (id) =>
        setStamps((s) => {
          const next = { ...s };
          delete next[id];
          return next;
        }),
    };
  }, [trip, stamps, update, add, remove, move, replaceStops, setEnds, clear, punch, unpunch, finish]);

  return <TripContext.Provider value={value}>{children}</TripContext.Provider>;
}

export function useTrip(): TripContextValue {
  const ctx = useContext(TripContext);
  if (!ctx) throw new Error('useTrip must be used inside TripProvider');
  return ctx;
}
